import React from "react";
import SkuProperties from "./SkuProperties";
import OrderItemDTO from "@/dto/order/OrderItemDTO";
import OrderDTO from "@/dto/order/OrderDTO";
import ShopType from "@/enum/ShopType";
import CustomizationColumn from "./CustomizationColumn";
import OrderItemTitle from "./OrderItemTitle";
import { observer } from "mobx-react-lite";
import OrderItemRowCheckbox from "./OrderItemRowCheckbox";
import CombineItemInfos from "./CombineItemInfos";
import { parseCombineItemInfos, parseOrderItemExtraInfo } from "@/utils/OrderUtils";
import OrderStatus from "@/enum/OrderStatus";
import AliCustomizationInfoItem from "./AliCustomizationInfoItem";
import { useGlobalStore } from "@/store/hooks";
import { Popover } from "antd";
import { Link } from "react-router-dom";
import OrderItemImage from "@/components/OrderItemImage";
import DouDianPeriodPurchaseInfo from "./DouDianPeriodPurchaseInfo";
import DouDianGiftLateDeliveryInfo from "./DouDianGiftLateDeliveryInfo";
import { affiliateMarketerShopTypes, notSupportProductManageShopTypes } from "@/utils/ShopUtils";

/**
 * 待付款、待发货：无
 * 已发货、已完成蓝色加粗
 * 已取消、退款中：红色加粗
 */
function getColorClass(orderItem: OrderItemDTO) {
    if (orderItem.isRefunding) {
        return "warn";
    }

    const status = orderItem.status;

    if (status === OrderStatus.PENDING_SHIPMENT || status === OrderStatus.PENDING_PAYMENT) {
        return null;
    }

    if (status === OrderStatus.SHIPPED || status === OrderStatus.SUCCESS) {
        return "text-success font-bold";
    }

    if (status === OrderStatus.CANCELED || orderItem.isRefunding) {
        return "text-danger font-bold";
    }

    return null;
}

function getStatusName(orderItem: OrderItemDTO) {
    switch (orderItem.status) {
        case OrderStatus.PENDING_SHIPMENT:
            return "待发货";
        case OrderStatus.PENDING_PAYMENT:
            return "未付款";
        case OrderStatus.SHIPPED:
            return "已发货";
        case OrderStatus.SUCCESS:
            return "交易成功";
        case OrderStatus.CANCELED:
            return "已取消";
        default:
            return null;
    }
}

function buildDiscountInfo(orderItem: any) {
    const buffer = [];
    const hasDiscounts = orderItem.discounts && orderItem.discounts > 0;
    if (hasDiscounts) {
        buffer.push("折扣：", orderItem.discounts, "元");
    }

    if (orderItem.adjustments && orderItem.adjustments > 0) {
        if (hasDiscounts) {
            buffer.push("，");
        }

        buffer.push("改价：", orderItem.adjustments, "元");
    }

    if (buffer.length > 0) {
        return `（${buffer.join("")}）`;
    }
}

interface OrderItemRowProps {
    order: OrderDTO;

    orderItem: OrderItemDTO;

    mergerOrder?: OrderDTO;

    shopType: ShopType;

    isSupplierShop?: boolean;
}

const OrderItemRow: React.FC<OrderItemRowProps> = function (props) {
    const { order, mergerOrder, orderItem, shopType, isSupplierShop } = props;

    const globalStore = useGlobalStore();
    const colorClass = getColorClass(orderItem);
    const statusName = getStatusName(orderItem);
    const discountsInfo = buildDiscountInfo(orderItem);

    const skuExternalId = orderItem.skuExternalId;
    const productExternalId = orderItem.productExternalId;

    const combineItemInfos = parseCombineItemInfos(orderItem);
    const orderItemExtraInfo = parseOrderItemExtraInfo(orderItem);

    function buildProductSettingLink() {
        const searchParams = new URLSearchParams({
            defaultShopId: order.shopId.toString(),
            fromOrderDetail: "1",
        });

        if (orderItem.productOriginalId) {
            searchParams.set("originalId", orderItem.productOriginalId);
        }

        const queryString = searchParams.toString();

        return queryString ? `/setting/product?${queryString}` : "/setting/product";
    }

    const productSettingLink = buildProductSettingLink();
    const canJumpToProductSetting =
        !notSupportProductManageShopTypes.includes(shopType) && !!orderItem.productOriginalId;

    function buildProductSettingAction(text: string, title: string) {
        if (!canJumpToProductSetting) {
            return null;
        }

        return (
            <Link to={productSettingLink} className="small text-light show-tooltip ml10 mr5" title={title}>
                [{text}]
            </Link>
        );
    }

    function buildAliCustomizationInfoItem() {
        if (order.aliOrderExtraInfo?.customizationInfo) {
            const customizationInfoItem = order.aliOrderExtraInfo.customizationInfo.find((it) => {
                return it.orderItemId === orderItem.originalId;
            });

            if (customizationInfoItem) {
                return <AliCustomizationInfoItem customizationInfoItem={customizationInfoItem} />;
            }
        }

        return null;
    }

    function buildDouDianAppointmentArrivalInfo() {
        if (order.doudianOrderExtraInfo?.appointmentArrivalInfo) {
            const inventory = order.doudianOrderExtraInfo.appointmentArrivalInfo.inventories?.find((it) => {
                return it.orderItemId === orderItem.originalId;
            });

            if (inventory?.warehouseName || inventory?.outWarehouseId) {
                const { warehouseName, outWarehouseId } = inventory;

                return (
                    <p>
                        仓库:
                        <span className="text-secondary">
                            {warehouseName}({outWarehouseId})
                        </span>
                    </p>
                );
            }
        }
    }

    function buildSuperOrderLinkInfo() {
        if (order.taobaoOrderExtraInfo?.superLinkOrder) {
            const superlinkOrderItem = order.taobaoOrderExtraInfo.superLinkOrder.superLinkOrderItems?.find((it) => {
                return it.orderItemId === orderItem.originalId;
            });

            if (superlinkOrderItem?.superlinkOrderSupplyPrice) {
                return (
                    <p>
                        超链商品价格:
                        <span className="text-secondary">{superlinkOrderItem.superlinkOrderSupplyPrice}元</span>
                    </p>
                );
            }
        }
    }

    return (
        <tr>
            <td>
                <OrderItemRowCheckbox order={order} orderItem={orderItem} mergerOrder={mergerOrder} />
            </td>

            <td>
                <OrderItemImage orderItem={orderItem} shopType={shopType} />

                <div className={colorClass}>
                    <span>{statusName}</span>

                    {orderItem.isRefunding ? <p>退款中</p> : null}
                </div>
            </td>

            <td className={colorClass}>
                <OrderItemTitle order={order} mergerOrder={mergerOrder} orderItem={orderItem} />

                <SkuProperties order={order} mergerOrder={mergerOrder} orderItem={orderItem} />

                {productExternalId && (
                    <p>
                        编码：<span className="text-secondary">{productExternalId}</span>
                    </p>
                )}

                {skuExternalId && (
                    <p>
                        Sku编码：<span className="text-secondary">{skuExternalId}</span>
                        {globalStore.warehouseWarningStore.getIsInventoryWarning(skuExternalId) ? (
                            <Popover
                                content={
                                    <div>
                                        商品库存状态:<span className="mr5 error">警戒</span>
                                        <Link to={`/features/warehouseManagement?externalId=${skuExternalId}`}>
                                            查看详情
                                        </Link>
                                    </div>
                                }
                            >
                                <span
                                    style={{
                                        background: "red",
                                        color: "white",
                                        marginLeft: "5px",
                                        display: "inline-block",
                                        width: "17px",
                                        textAlign: "center",
                                        height: "19px",
                                        borderRadius: "5px",
                                        cursor: "default",
                                    }}
                                >
                                    警
                                </span>
                            </Popover>
                        ) : null}
                    </p>
                )}

                {orderItem.stockNo && (
                    <p>
                        仓库/货位: <span className="text-secondary">{orderItem.stockNo}</span>
                    </p>
                )}

                {orderItem.skuOriginalId && (
                    <p>
                        SkuID: <span className="text-secondary">{orderItem.skuOriginalId}</span>
                    </p>
                )}

                {orderItem.productOriginalId && (
                    <p>
                        商品ID: <span className="text-secondary">{orderItem.productOriginalId}</span>
                    </p>
                )}

                {!!orderItem.weight ? (
                    <p>
                        重量：<span className="text-secondary">{(orderItem.weight * orderItem.quantity) / 1000}kg</span>
                        {buildProductSettingAction("修改", "修改商品重量")}
                    </p>
                ) : (
                    <p>
                        重量：<span className="text-secondary">未设置</span>
                        {buildProductSettingAction("编辑重量", "编辑商品重量")}
                    </p>
                )}

                {orderItem.affiliateMarketerName || orderItem.affiliateMarketerId ? (
                    <p>
                        订单来源：带货达人
                        {orderItem.affiliateMarketerName ? (
                            <span className="text-secondary">-{orderItem.affiliateMarketerName}</span>
                        ) : null}
                    </p>
                ) : affiliateMarketerShopTypes.includes(shopType) ? (
                    <p>
                        订单来源：<span className="text-secondary">非带货达人订单</span>
                    </p>
                ) : null}

                {combineItemInfos ? (
                    <CombineItemInfos combineItemInfos={combineItemInfos} orderItemStatus={orderItem.status} />
                ) : null}

                {orderItem.shippingTime && (
                    <p>
                        发货时间: <span className="text-secondary">{orderItem.shippingTime}</span>
                    </p>
                )}

                {orderItemExtraInfo?.imeis?.length ? (
                    <p>
                        手机串号: <span className="text-secondary">{orderItemExtraInfo.imeis.join(",")}</span>
                    </p>
                ) : null}

                {orderItemExtraInfo?.imei2s?.length ? (
                    <p>
                        手机串号2: <span className="text-secondary">{orderItemExtraInfo.imei2s.join(",")}</span>
                    </p>
                ) : null}

                {orderItemExtraInfo?.deviceSns?.length ? (
                    <p>
                        SN码: <span className="text-secondary">{orderItemExtraInfo.deviceSns.join(",")}</span>
                    </p>
                ) : null}

                {orderItemExtraInfo?.eanCodes?.length ? (
                    <p>
                        EAN码/69码: <span className="text-secondary">{orderItemExtraInfo.eanCodes.join(",")}</span>
                    </p>
                ) : null}

                {buildDouDianAppointmentArrivalInfo()}
                <DouDianPeriodPurchaseInfo order={order} orderItem={orderItem} />

                <DouDianGiftLateDeliveryInfo order={order} orderItem={orderItem} />

                {buildSuperOrderLinkInfo()}
            </td>

            <td>
                <div>
                    {`${orderItem.price || 0}元 × ${orderItem.quantity}`}
                    {orderItem.canceledQuantity > 0 ? (
                        <span className="text-danger font-bold ml10 fs11">
                            （取消数量：{orderItem.canceledQuantity}）
                        </span>
                    ) : null}
                    <span>{discountsInfo}</span>
                </div>

                {orderItem.cost && <div>成本价: {orderItem.cost}元</div>}
            </td>

            {/* 定制信息 */}
            <td>
                <CustomizationColumn order={order} orderItem={orderItem} isSupplierShop={isSupplierShop} />

                {buildAliCustomizationInfoItem()}
            </td>
        </tr>
    );
};

export default observer(OrderItemRow);
